import React, { useState, useEffect, useCallback } from 'react';
import { clientPortalApi, dealsApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserCircle, Plus, Copy, LinkSimple, Trash, Eye, ChatCircle, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

export default function ClientPortalPage() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    deal_id: '', access_level: 'read_only', guest_name: '', guest_email: '', expires_in_days: 30,
  });

  const fetchData = useCallback(async () => {
    try {
      const [invRes, dealsRes] = await Promise.all([
        clientPortalApi.getAll(),
        dealsApi.getAll({ limit: 200 }),
      ]);
      setInvites(invRes.data);
      setDeals(dealsRes.data);
    } catch (error) {
      logger.error(error);
      toast.error('Failed to load portal invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.deal_id) return toast.error('Please select a deal');
    try {
      await clientPortalApi.create({ ...form, expires_in_days: parseInt(form.expires_in_days, 10) || 0 });
      toast.success('Portal link created');
      setIsOpen(false);
      setForm({ deal_id: '', access_level: 'read_only', guest_name: '', guest_email: '', expires_in_days: 30 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this portal link? Guests will immediately lose access.')) return;
    try {
      await clientPortalApi.revoke(id);
      toast.success('Portal link revoked');
      fetchData();
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/portal/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Portal link copied to clipboard');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  if (!isAdmin) {
    return (
      <div className="text-center p-12">
        <UserCircle size={48} className="mx-auto text-muted-foreground mb-3 opacity-40" />
        <p className="text-muted-foreground">Admin/Manager access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-portal-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Client Portal</h1>
          <p className="text-muted-foreground text-sm mt-1">Generate secure external B2B links to share deals &amp; documents</p>
        </div>
        <Button onClick={() => setIsOpen(true)} className="rounded-lg" data-testid="new-invite-btn">
          <Plus size={18} className="mr-2" /> New Portal Link
        </Button>
      </div>

      {loading ? (
        <div className="text-center p-12 text-muted-foreground">Loading…</div>
      ) : invites.length === 0 ? (
        <div className="text-center p-12 border border-dashed border-border rounded-xl">
          <LinkSimple size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
          <p className="text-muted-foreground">No portal links created yet</p>
          <p className="text-xs text-muted-foreground mt-1">Create one to invite an external party to view a deal</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Guest</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Deal</th>
                <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Access</th>
                <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Views</th>
                <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Expires</th>
                <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Status</th>
                <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id} className="border-b border-border hover:bg-muted/30" data-testid={`invite-${inv.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-sm">{inv.guest_name || 'Unnamed guest'}</p>
                    <p className="text-xs text-muted-foreground">{inv.guest_email || '-'}</p>
                  </td>
                  <td className="p-3 text-sm">{inv.deal_title || '-'}</td>
                  <td className="p-3 text-center">
                    {inv.access_level === 'interactive' ? (
                      <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"><ChatCircle size={12} className="mr-1" /> Interactive</Badge>
                    ) : (
                      <Badge variant="outline"><Eye size={12} className="mr-1" /> Read-only</Badge>
                    )}
                  </td>
                  <td className="p-3 text-center text-sm">{inv.view_count || 0}</td>
                  <td className="p-3 text-center text-xs text-muted-foreground">
                    {inv.expires_at ? (
                      <span className="flex items-center justify-center gap-1"><Clock size={12} /> {formatDate(inv.expires_at)}</span>
                    ) : '—'}
                  </td>
                  <td className="p-3 text-center">
                    {inv.is_active ? (
                      <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="opacity-60">Revoked</Badge>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      {inv.is_active && (
                        <Button variant="outline" size="sm" onClick={() => copyLink(inv.token)} data-testid={`copy-${inv.id}`}>
                          <Copy size={14} className="mr-1" /> Copy
                        </Button>
                      )}
                      {inv.is_active && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleRevoke(inv.id)} data-testid={`revoke-${inv.id}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create Portal Link</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Deal *</Label>
              <Select value={form.deal_id} onValueChange={(v) => setForm({ ...form, deal_id: v })}>
                <SelectTrigger data-testid="deal-select"><SelectValue placeholder="Select a deal" /></SelectTrigger>
                <SelectContent>
                  {deals.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Access Level</Label>
              <Select value={form.access_level} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="read_only">Read-only (View deal &amp; docs)</SelectItem>
                  <SelectItem value="interactive">Interactive (+ send messages)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Guest Name</Label>
                <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="guest-name" />
              </div>
              <div className="space-y-2">
                <Label>Guest Email</Label>
                <Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="guest-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expires In (days, 0 = never)</Label>
              <Input type="number" min="0" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} data-testid="expires-in" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="invite-submit">Create Link</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
